<template>
  <v-bottom-sheet
    v-model="isOpen"
    height="auto"
    :scrim="true"
    class="mobile-filter-sheet"
  >
    <v-card class="mobile-filter-sheet__card">
      <!-- Swipe handle -->
      <div class="mobile-filter-sheet__handle">
        <div class="mobile-filter-sheet__handle-bar" />
      </div>

      <!-- Header -->
      <header class="mobile-filter-sheet__header">
        <div class="mobile-filter-sheet__title">
          <v-icon size="20" color="primary">mdi-tune-variant</v-icon>
          <h2>Filter Content</h2>
        </div>
        <button class="mobile-filter-sheet__close" @click="close">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>

      <v-divider />

      <!-- Content -->
      <div class="mobile-filter-sheet__content">
        <div class="mobile-filter-sheet__filters">
          <button
            v-for="filter in contentFilters"
            :key="filter.value"
            class="mobile-filter-sheet__filter"
            :class="{ 'mobile-filter-sheet__filter--active': selectedFilter === filter.value }"
            @click="selectFilter(filter.value)"
          >
            <v-icon size="20">{{ filter.icon }}</v-icon>
            <span class="mobile-filter-sheet__filter-label">{{ filter.label }}</span>
            <span v-if="filter.count" class="mobile-filter-sheet__filter-count">{{ filter.count }}</span>
            <v-icon
              v-if="selectedFilter === filter.value"
              size="18"
              class="mobile-filter-sheet__filter-check"
            >
              mdi-check
            </v-icon>
          </button>
        </div>

        <!-- Reset button -->
        <button
          v-if="selectedFilter !== 'all'"
          class="mobile-filter-sheet__reset-btn"
          @click="resetFilter"
        >
          <v-icon size="16">mdi-refresh</v-icon>
          Reset to All
        </button>
      </div>
    </v-card>
  </v-bottom-sheet>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { FeedFilter } from "@/types";

// Props
const props = defineProps<{
  modelValue: boolean;
  selectedFilter: FeedFilter;
}>();

// Emits
const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "filter-change": [filter: FeedFilter];
}>();

// State
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// Filter options
interface ContentFilter {
  value: FeedFilter;
  label: string;
  icon: string;
  count?: number;
}

const contentFilters: ContentFilter[] = [
  { value: "all", label: "All Content", icon: "mdi-view-grid-outline" },
  { value: "songs", label: "Songs", icon: "mdi-music" },
  { value: "videos", label: "Videos", icon: "mdi-video-outline" },
  { value: "podcasts", label: "Podcasts", icon: "mdi-podcast" },
  { value: "bursts", label: "Bursts", icon: "mdi-lightning-bolt" },
];

// Methods
function close() {
  isOpen.value = false;
}

function selectFilter(filter: FeedFilter) {
  emit("filter-change", filter);
  // Slight delay before closing for visual feedback
  setTimeout(() => {
    close();
  }, 150);
}

function resetFilter() {
  selectFilter("all");
}
</script>

<style scoped lang="scss">
.mobile-filter-sheet {
  :deep(.v-bottom-sheet__content) {
    border-radius: 24px 24px 0 0;
    overflow: hidden;
  }

  &__card {
    background: rgb(var(--v-theme-surface));
    border-radius: 24px 24px 0 0;
    padding-bottom: env(safe-area-inset-bottom, 16px);
  }

  &__handle {
    display: flex;
    justify-content: center;
    padding: 12px 0 8px;
  }

  &__handle-bar {
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: rgba(var(--v-theme-on-surface), 0.2);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px 16px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;

    h2 {
      font-family: var(--font-display);
      font-size: 1.125rem;
      font-weight: 600;
      margin: 0;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 50%;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__content {
    padding: 16px;
  }

  &__filters {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__filter {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 16px;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    background: rgba(var(--v-theme-on-surface), 0.02);
    border-radius: 14px;
    font-family: var(--font-body);
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;

    .v-icon:first-child {
      color: rgba(var(--v-theme-on-surface), 0.5);
    }

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
      border-color: rgba(var(--v-theme-on-surface), 0.12);
    }

    &:active {
      transform: scale(0.98);
    }

    &--active {
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-primary), 0.12),
        rgba(var(--v-theme-primary), 0.04)
      );
      border-color: rgba(var(--v-theme-primary), 0.3);

      .v-icon:first-child {
        color: rgb(var(--v-theme-primary));
      }

      .mobile-filter-sheet__filter-label {
        color: rgb(var(--v-theme-primary));
        font-weight: 600;
      }
    }
  }

  &__filter-label {
    flex: 1;
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
  }

  &__filter-count {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.4);
    background: rgba(var(--v-theme-on-surface), 0.05);
    padding: 2px 8px;
    border-radius: 10px;
  }

  &__filter-check {
    color: rgb(var(--v-theme-primary));
  }

  &__reset-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px;
    margin-top: 16px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgb(var(--v-theme-on-surface));
    }

    &:active {
      transform: scale(0.98);
    }
  }
}

// Light theme
.v-theme--light {
  .mobile-filter-sheet {
    &__filter {
      background: rgba(0, 0, 0, 0.01);
      border-color: rgba(0, 0, 0, 0.06);

      &:hover {
        background: rgba(0, 0, 0, 0.03);
      }

      &--active {
        background: linear-gradient(
          135deg,
          rgba(var(--v-theme-primary), 0.1),
          rgba(var(--v-theme-primary), 0.03)
        );
      }
    }

    &__reset-btn {
      background: rgba(0, 0, 0, 0.03);

      &:hover {
        background: rgba(0, 0, 0, 0.06);
      }
    }
  }
}
</style>
